import type {FC} from 'react';
import React, {useCallback} from 'react';
import {IntlProvider} from 'react-intl';
import {AuthProvider} from 'react-oidc-context';
import {Provider} from 'react-redux';
import {BrowserRouter, useNavigate} from 'react-router';
import type {Mapper} from '@automapper/core';
import {useAuthUrl, useAutoLoginLogout} from '@igp/shared/auth';
import {AutoMapperProvider} from '@igp/shared/auto-mapper';
import {BreadcrumbsProvider} from '@igp/shared/components/breadcrumb';
import {EditableFormProvider} from '@igp/shared/components/editable';
import {ErrorUnstyled} from '@igp/shared/components/error';
import {ModalProvider} from '@igp/shared/components/modal';
import {CircularProgress} from '@igp/shared/components/progress';
import {TopBar} from '@igp/shared/components/top-bar';
import {currencyFormats} from '@igp/shared/intl';
import {InversifyProvider, useService} from '@igp/shared/ioc-container';
import {createLocaleHoc, useLocale, useLocalizationResources} from '@igp/shared/localization';
import type {ITracingService} from '@igp/shared/otel';
import {TraceProvider, TracingServiceSymbol, withTraceErrorBoundary} from '@igp/shared/otel';
import {AsyncActionLocationTracker} from '@igp/shared/redux/async-action';
import {EntityContext} from '@igp/shared/redux/entity';
import type {CustomTheme, Locale} from '@igp/shared/style';
import {CustomThemeProvider} from '@igp/shared/style';
import {UserSettingsProvider} from '@igp/shared/user-settings';
import {schemaMapper} from '@models';
import type {Container} from 'inversify';
import type {Store} from 'redux';
import {compose} from 'redux';
import {makeStyles} from 'tss-react/mui';
import type {RootAction, RootState} from 'typesafe-actions';

import {CurrentBalance} from '@components/CurrentBalance';
import {ServiceTypes} from '@ioc-container';
import {AccessControlProvider} from '@access-control';

import {type Config, ConfigurationProvider, getUserManagerSettings} from '../configuration';

import {AgentBoMenu} from './components/AgentBoMenu';
import {Notifications} from './components/Notifications';
import AppRouter from './router/Router';
import {initDatabase} from './dbContext';
type AppProps = {
    container: Container;
    config: Config;
    store: Store<RootState, RootAction>;
};

function InternalApp(): React.ReactNode {
    const {isAuthenticated, isLoading} = useAutoLoginLogout();
    const {hasAuthQueryParams} = useAuthUrl();

    return isAuthenticated && !isLoading && !hasAuthQueryParams ? <ProtectedApp /> : <CircularProgress position="center" />;
}

const useStyles = makeStyles()((theme: CustomTheme) => ({
    appRoot: {
        display: 'flex',
    },
    appModule: {
        flexGrow: 1,
        position: 'absolute',
        left: theme.custom.sideBarWidth,
        top: 0,
        right: 0,
        bottom: 0,
        display: 'flex',
        flexDirection: 'column',
        overflowX: 'hidden',
        background: theme.custom.palette.content.backgroundLight,

        [theme.breakpoints.down('md')]: {
            left: 0,
        },
    },
    appModuleMinimized: {
        left: theme.custom.sideBarMinimizedWidth,
    },
}));

function ProtectedApp() {
    const {classes, cx} = useStyles();
    const [sidebarStick, setSidebarStick] = React.useState(true);

    return (
        <div className={classes.appRoot}>
            <AgentBoMenu isStick={sidebarStick} onIsStickChange={() => setSidebarStick(!sidebarStick)} />
            <Notifications />
            <main className={cx(classes.appModule, !sidebarStick && classes.appModuleMinimized)}>
                <TopBar>
                    <CurrentBalance />
                </TopBar>
                <AppRouter />
            </main>
        </div>
    );
}

const messagesMap: Record<Locale, () => Promise<unknown>> = {
    en: () => import('../../compiled-lang/en.json'),
    'zh-CN': () => import('../../compiled-lang/zh-CN.json'),
};

const defaultLocale: Locale = 'en';
const withLocale = createLocaleHoc(defaultLocale);

function withLocalization(WrappedComponent: FC<AppProps>) {
    return function LocalizedHOC(props: AppProps) {
        const {locale} = useLocale();
        const {messages} = useLocalizationResources({messagesMap});

        return (
            <IntlProvider
                defaultLocale={defaultLocale}
                locale={locale ?? defaultLocale}
                messages={messages}
                timeZone="Etc/UTC"
                formats={{number: currencyFormats}}
                defaultRichTextElements={{
                    strong: text => <strong>{text}</strong>,
                    /**
                     * Self-closing tag is broken in defaultRichTextElements
                     * @see https://github.com/formatjs/formatjs/issues/3982
                     */
                    // @ts-expect-error: foramtjs typing issue with <br />
                    br: <br />,
                }}
            >
                <WrappedComponent {...props} />
            </IntlProvider>
        );
    };
}

function withCustomTheme(WrappedComponent: FC<AppProps>) {
    return function CustomThemeHOC(props: AppProps) {
        const {locale} = useLocale();
        return (
            <CustomThemeProvider locale={locale as Locale}>
                <WrappedComponent {...props} />
            </CustomThemeProvider>
        );
    };
}

function withReduxTheme(WrappedComponent: FC<AppProps>) {
    return function ReduxHOC(props: AppProps) {
        return (
            <Provider store={props.store}>
                <AsyncActionLocationTracker />
                <WrappedComponent {...props} />
            </Provider>
        );
    };
}

function withAuth(WrappedComponent: FC<AppProps>) {
    return function AuthHOC({container, config, store}: AppProps) {
        const navigate = useNavigate();
        const {authUrl, authClientId, authSilentRefreshPage} = config;
        const settings = getUserManagerSettings({authUrl, clientId: authClientId, silentRefreshPage: authSilentRefreshPage});
        const {relativeRedirectUrl, absoluteRedirectUrl} = useAuthUrl(settings?.redirect_uri);

        const handleSignIn = useCallback(() => {
            //remove oidc query string params
            navigate(relativeRedirectUrl, {replace: true});
        }, [relativeRedirectUrl]);

        return (
            <AuthProvider {...settings} redirect_uri={absoluteRedirectUrl} onSigninCallback={handleSignIn}>
                <WrappedComponent container={container} config={config} store={store}></WrappedComponent>
            </AuthProvider>
        );
    };
}

function withStrictMode(WrappedComponent: FC<AppProps>) {
    return function StrictModeHOC(props: AppProps) {
        return (
            <React.StrictMode>
                <WrappedComponent {...props} />
            </React.StrictMode>
        );
    };
}

function withAutomapper(WrappedComponent: FC<AppProps>) {
    return function AutomapperHOC(props: AppProps) {
        const mapper = useService<Mapper>(ServiceTypes.AutoMapper);
        return (
            <AutoMapperProvider mapper={mapper}>
                <WrappedComponent {...props} />
            </AutoMapperProvider>
        );
    };
}

function withRouter(WrappedComponent: FC<AppProps>) {
    return function RouterHOC(props: AppProps) {
        return (
            <BrowserRouter>
                <WrappedComponent {...props} />
            </BrowserRouter>
        );
    };
}

function withInversify(WrappedComponent: FC<AppProps>) {
    return function InversifyHOC(props: AppProps) {
        return (
            <InversifyProvider container={props.container}>
                <WrappedComponent {...props} />
            </InversifyProvider>
        );
    };
}

function withTracing(WrappedComponent: FC<AppProps>) {
    return function TracingHOC(props: AppProps) {
        const tracingService = props.container.get<ITracingService>(TracingServiceSymbol);
        return (
            <TraceProvider tracingService={tracingService}>
                <WrappedComponent {...props} />
            </TraceProvider>
        );
    };
}
function withConfig(WrappedComponent: FC<AppProps>) {
    return function ConfigHOC(props: AppProps) {
        return (
            <ConfigurationProvider config={props.config}>
                <WrappedComponent {...props} />
            </ConfigurationProvider>
        );
    };
}
function withUserSettings(WrappedComponent: FC<AppProps>) {
    return function UserSettingsHOC(props: AppProps) {
        const storage = initDatabase();
        return (
            <UserSettingsProvider storage={storage}>
                <WrappedComponent {...props} />
            </UserSettingsProvider>
        );
    };
}

function withEntity(WrappedComponent: FC<AppProps>) {
    return function EntityHOC(props: AppProps) {
        return (
            <EntityContext.Provider value={{schemaMapper}}>
                <WrappedComponent {...props} />
            </EntityContext.Provider>
        );
    };
}

function withModal(WrappedComponent: FC<AppProps>) {
    return function ModalHOC(props: AppProps) {
        return (
            <ModalProvider>
                <WrappedComponent {...props} />
            </ModalProvider>
        );
    };
}

function withBreadcrumbs(WrappedComponent: FC<AppProps>) {
    return function withBreadcrumbsHOC(props: AppProps) {
        return (
            <BreadcrumbsProvider>
                <WrappedComponent {...props} />
            </BreadcrumbsProvider>
        );
    };
}
function withEditableProvider(WrappedComponent: FC<AppProps>) {
    return function withEditableProviderHOC(props: AppProps) {
        return (
            <EditableFormProvider>
                <WrappedComponent {...props} />
            </EditableFormProvider>
        );
    };
}

function withAccessControlProvider(WrappedComponent: FC<AppProps>) {
    return function withAccessControlProviderHOC(props: AppProps) {
        return (
            <AccessControlProvider>
                <WrappedComponent {...props} />
            </AccessControlProvider>
        );
    };
}

export const App = withStrictMode(
    withRouter(
        withTracing(
            withTraceErrorBoundary(
                compose(
                    withConfig,
                    withAuth,
                    withUserSettings,
                    withReduxTheme,
                    withInversify,
                    withEntity,
                    withLocale,
                    withCustomTheme,
                    withLocalization,
                    withAutomapper,
                    withEditableProvider,
                    withBreadcrumbs,
                    withAccessControlProvider,
                    withModal
                )(InternalApp),
                ErrorUnstyled
            )
        )
    )
);
